import { Button, Input } from "@/components";
import { RefObject, useCallback, useState } from "react";
import { UserAvatar } from "@/components/User/UserAvatar";
import { useTranslate, useUser } from "@/providers/context";
import { cn } from "@/utils";
import { ThreadIcon } from "@/icons";
import { useUserDetails } from "@/hooks";
import { useCommentsContext } from "../providers/context/useCommentsContext";

interface CommentInputProps {
  putComment: (comment: string) => void;
  onCancel?: () => void;
  topLevel?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  isEditing?: boolean;
  initialValue?: string;
  isSaving?: boolean;
}

export const CommentInput = ({
  putComment,
  onCancel,
  topLevel,
  inputRef,
  isEditing,
  initialValue,
  isSaving,
}: CommentInputProps) => {
  const { user_name } = useUser();
  const { data } = useUserDetails();
  const [comment, setComment] = useState<string>(initialValue ?? "");
  const { translate } = useTranslate();
  const { setCommentToEdit } = useCommentsContext();

  const handleCancel = useCallback(() => {
    setComment("");
    if (onCancel) {
      onCancel();
    }
  }, [onCancel, setComment]);

  const handlePutComment = useCallback(
    (comment: string) => {
      putComment(comment);
      if (!isEditing) {
        handleCancel();
      }
    },
    [putComment, handleCancel, isEditing],
  );

  return (
    <div
      className={cn(
        "relative flex w-full gap-3",
        topLevel ? "mt-0.5" : "border-l-[2px] border-transparent pl-7",
        isEditing && "mt-2 pl-0",
      )}
    >
      {!isEditing && (
        <UserAvatar name={user_name} imageUrl={data?.avatar_url} />
      )}
      <div className="flex-grow">
        <Input
          ref={inputRef}
          size="s"
          type="text"
          placeholder={translate("Add a comment")}
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
          }}
          onFocus={() => {
            if (!isEditing) {
              setCommentToEdit(undefined);
            }
          }}
        ></Input>
        <div className="flex justify-end">
          {(!!comment || !!onCancel) && (
            <div className="mt-5">
              <Button variant="tertiary" onClick={handleCancel}>
                {translate("Cancel")}
              </Button>
              <Button
                className="ml-4 text-grey50"
                onClick={() => handlePutComment(comment)}
                disabled={!comment || comment === initialValue || isSaving}
                loading={isSaving}
              >
                {translate(isEditing ? "Save" : "Comment")}
              </Button>
            </div>
          )}
        </div>
      </div>
      {!topLevel && !isEditing && (
        <div className="absolute left-[-2px] top-0 text-border">
          <ThreadIcon className="h-[20px] w-[16px]" />
        </div>
      )}
    </div>
  );
};
